import { HttpStatus } from '../../constants'
import { ErrorCode } from './errorCode'

const defaultBadRequestMessage = 'The given request was not valid'
const defaultErrorMessage = 'Sorry, an unexpected error occurred'
const misconfiguredCalendarIntegrationMessage = 'The given specialist is not properly configured in our system'
const defaultUnableToScheduleErrorMessage = 'Unable to schedule appointment, please contact your system administrator'

const httpStatusToDisplayTextMap = (statusCode: HttpStatus) => {
  if (statusCode && statusCode >= 400 && statusCode < 500) {
    return defaultBadRequestMessage
  }

  return defaultErrorMessage
}

const errorCodeToDisplayTextMap: Partial<Record<ErrorCode, string>> = {
  BAD_REQUEST: defaultBadRequestMessage,
  BOOKING_RESERVED_SLOT_NOT_ALLOWED: 'This time slot was reserved by another customer and is no longer available',
  DOUBLE_BOOKING_HEARING_AID_EVALUATION_NOT_ALLOWED: 'This customer and opportunity already have a Hearing Aid Evaluation appointment',
  FAILED_TO_CREATE_APPOINTMENT_IN_TCM: `Failed to book appointment in TCM`,
  FAILED_TO_FETCH_RESOURCE_CALENDAR_INTEGRATION_AVAILABILITIES: `Unable to determine availability in TCM`,
  FAILED_TO_GET_TCM_RESOURCES_FOR_CLINIC: `Unable to fetch shop resources from TCM`,
  FORBIDDEN: `The given request is not allowed`,
  INTERNAL_SERVER_ERROR: defaultErrorMessage,
  INVALID_APPOINTMENT_STATUS_UPDATE: `The requested appointment update is not permitted`,
  INVALID_CUSTOMER_ASSIGNMENT: `The given customer is missing required information`,
  INVALID_DATE_RANGE: `The requested date range was not valid`,
  INVALID_DURATION_IN_MINUTES: `This appointment's duration in minutes is not valid`,
  INVALID_RESOURCE: `The requested specialist is not valid`,
  LEAD_RESERVATION_ALREADY_EXISTS: 'A reservation for this Lead already exists',
  PROVIDER_HISTORY_UNAVAILABLE:
    "We couldn't check whether this customer has already seen a provider. Nothing was changed. Please try again.",
  REQUESTED_TIME_SLOT_UNAVAILABLE: 'This time slot is no longer available',
  RESERVATION_NOT_FOUND: 'Reservation was not found with the specified information',
  RESERVATION_STATUS_CHANGE: 'The reservation status cannot be changed',
  RESERVATION_EXPIRED: 'This reservation is expired',
  RESERVATION_CONVERT_ERROR: 'There was an error trying to schedule an appointment from this reservation',
  RESERVATION_RESOURCE_NOT_FOUND: 'One or more resources selected for this reservation do not exist',
  RESOURCE_APPOINTMENT_DURATION_UNKNOWN: `The given specialist's appointment duration is unknown`,
  RESOURCE_CALENDAR_INTEGRATION_INVALID_SHOP_ID: misconfiguredCalendarIntegrationMessage,
  RESOURCE_CALENDAR_INTEGRATION_NOT_CONFIGURED: misconfiguredCalendarIntegrationMessage,
  RESOURCE_CALENDAR_INTEGRATION_NOT_FOUND: misconfiguredCalendarIntegrationMessage,
  RESOURCE_RESERVATION_ALREADY_EXISTS: `The requested date and time is no longer available`,
  START_DATE_IN_PAST: `The requested date and time is in the past`,
  START_DATE_NO_LONGER_AVAILABLE: `The requested date and time is no longer available`,
  SYCLE_PRO_FAILED_TO_AUTHENTICATE: defaultUnableToScheduleErrorMessage,
  SYCLE_PRO_FAILED_TO_CREATE_APPOINTMENT: defaultUnableToScheduleErrorMessage,
  SYCLE_PRO_FAILED_TO_PROCESS_ARCHIVED_PATIENT:
    'Unable to schedule appointment, please contact your system administrator. Error: Archived Patient in Sycle.',
  SYCLE_PRO_FAILED_TO_SEARCH_PATIENTS: defaultUnableToScheduleErrorMessage,
  SYCLE_PRO_APPOINTMENT_TYPE_NOT_FOUND:
    'Unable to schedule appointment, please contact your system administrator. Error: Appointment type not found in Sycle.',
  SYCLE_PRO_FAILED_TO_UPDATE_APPOINTMENT: defaultUnableToScheduleErrorMessage,
  TCM_FAILED_TO_CREATE_PATIENT: `Failed to create patient in TCM`,
  TCM_FAILED_TO_UPDATE_APPOINTMENT: `Unable to update this appointment in TCM`,
  TCM_INVALID_COUNTRY: `The given country is not supported by TCM`,
  TCM_RESPONSE_ERROR: `Sorry, we encountered an error while communicating with TCM`,
} as const

const errorCodeToDisplayText = (code: ErrorCode, statusCode: HttpStatus, fallbackMessage: string = defaultErrorMessage) =>
  errorCodeToDisplayTextMap[code] ?? httpStatusToDisplayTextMap(statusCode) ?? fallbackMessage

export { errorCodeToDisplayText }
